import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  PiggyBank,
  CreditCard,
  Target,
  BookOpen,
  CheckCircle,
  Bell,
  Calendar,
  DollarSign
} from "lucide-react";

/**
 * Smart Financial Alerts Component
 * 
 * Displays intelligent financial notifications and alerts based on
 * user behavior, spending patterns, and financial goals
 */

// Mock alert data - replace with real-time financial monitoring
const mockAlerts = [
  {
    id: 1,
    type: "budget_warning",
    priority: "high",
    title: "Dining Budget Alert",
    message: "You've used 85% of your dining budget with 8 days left in the month",
    amount: 340,
    budgetTotal: 400,
    category: "dining",
    icon: AlertTriangle,
    color: "text-red-600 bg-red-50 border-red-200",
    action: "Adjust Spending",
    lessonSuggestion: "Budget Control Strategies",
    timestamp: "2 hours ago",
    isNew: true
  },
  {
    id: 2,
    type: "savings_milestone",
    priority: "medium",
    title: "Savings Goal Achievement",
    message: "Congratulations! You've reached 50% of your emergency fund goal",
    amount: 2000,
    budgetTotal: 4000,
    category: "savings",
    icon: TrendingUp,
    color: "text-green-600 bg-green-50 border-green-200",
    action: "Celebrate",
    lessonSuggestion: null,
    timestamp: "1 day ago",
    isNew: true
  },
  {
    id: 3,
    type: "investment_opportunity",
    priority: "medium",
    title: "Investment Opportunity",
    message: "You have $500 surplus this month. Consider investing for long-term growth",
    amount: 500,
    budgetTotal: null,
    category: "investment",
    icon: TrendingUp,
    color: "text-blue-600 bg-blue-50 border-blue-200",
    action: "Explore Options",
    lessonSuggestion: "Investment Basics",
    timestamp: "3 hours ago",
    isNew: true
  },
  {
    id: 4,
    type: "bill_reminder",
    priority: "high",
    title: "Upcoming Bill Due",
    message: "Credit card payment of $280 is due in 3 days",
    amount: 280,
    budgetTotal: null,
    category: "bills",
    icon: Calendar,
    color: "text-orange-600 bg-orange-50 border-orange-200",
    action: "Pay Now",
    lessonSuggestion: "Credit Management",
    timestamp: "5 hours ago",
    isNew: false
  },
  {
    id: 5,
    type: "cashback_earned",
    priority: "low",
    title: "Cashback Earned",
    message: "You earned $12.50 in cashback rewards this week",
    amount: 12.50,
    budgetTotal: null,
    category: "rewards",
    icon: CheckCircle,
    color: "text-purple-600 bg-purple-50 border-purple-200",
    action: "View Details",
    lessonSuggestion: "Rewards Optimization",
    timestamp: "1 day ago",
    isNew: false
  }
];

const SmartFinancialAlerts = () => {
  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  // Get action button variant
  const getActionVariant = (priority: string) => {
    switch (priority) {
      case "high": return "default";
      case "medium": return "outline";
      case "low": return "ghost";
      default: return "outline";
    }
  };

  const newAlertsCount = mockAlerts.filter(alert => alert.isNew).length;

  return (
    <Card className="bg-background border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-secondary" />
            <CardTitle className="text-lg font-semibold text-gray-900">
              Smart Financial Alerts
            </CardTitle>
            {newAlertsCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {newAlertsCount} new
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="sm" className="text-xs">
            Mark all as read
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          AI-powered notifications to keep your finances on track
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {mockAlerts.map((alert) => {
            const AlertIcon = alert.icon;
            
            return (
              <div 
                key={alert.id}
                className={`p-4 border rounded-lg transition-all duration-200 hover:shadow-sm ${
                  alert.isNew ? 'bg-blue-50/30 border-blue-200' : 'border-gray-100'
                } ${alert.color.includes('bg-') ? '' : 'hover:' + alert.color.split(' ')[2]}`}
              >
                <div className="flex items-start gap-3">
                  {/* Alert Icon */}
                  <div className={`p-2 rounded-lg ${alert.color.split(' ')[1]} ${alert.color.split(' ')[2]}`}>
                    <AlertIcon className={`h-4 w-4 ${alert.color.split(' ')[0]}`} />
                  </div>

                  {/* Alert Content */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{alert.title}</h4>
                          <Badge variant="outline" className={`text-xs ${getPriorityColor(alert.priority)}`}>
                            {alert.priority}
                          </Badge>
                          {alert.isNew && (
                            <Badge variant="destructive" className="text-xs">NEW</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{alert.message}</p>
                        
                        {/* Amount Display */}
                        {alert.amount && (
                          <div className="flex items-center gap-1 text-sm">
                            <DollarSign className="h-3 w-3 text-gray-500" />
                            <span className="font-medium text-gray-900">
                              {alert.amount.toFixed(2)}
                            </span>
                            {alert.budgetTotal && (
                              <span className="text-gray-500">
                                of ${alert.budgetTotal.toFixed(2)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Timestamp */}
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {alert.timestamp}
                      </span>
                    </div>

                    {/* Progress Bar for Budget Alerts */}
                    {alert.type === "budget_warning" && alert.budgetTotal && (
                      <div className="space-y-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (alert.amount / alert.budgetTotal) * 100 >= 90 ? 'bg-red-500' :
                              (alert.amount / alert.budgetTotal) * 100 >= 75 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${(alert.amount / alert.budgetTotal) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          {((alert.amount / alert.budgetTotal) * 100).toFixed(0)}% of budget used
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-2">
                      <Button 
                        variant={getActionVariant(alert.priority)} 
                        size="sm" 
                        className="h-7 px-3 text-xs"
                      >
                        {alert.action}
                      </Button>
                      
                      {alert.lessonSuggestion && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 px-3 text-xs bg-secondary/5 border-secondary/20 hover:bg-secondary/10"
                        >
                          <BookOpen className="h-3 w-3 mr-1" />
                          Learn: {alert.lessonSuggestion}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Alert Summary */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <span className="text-gray-600">
                {mockAlerts.filter(a => a.priority === "high").length} high priority
              </span>
              <span className="text-gray-600">
                {mockAlerts.filter(a => a.priority === "medium").length} medium priority
              </span>
            </div>
            <Button variant="outline" size="sm" className="text-xs">
              View Alert Settings
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartFinancialAlerts;